
import { useEffect, useState } from 'react';
import { Task, TaskPriority, TaskStatus } from '@/types/task';
import { useTasks } from '@/hooks/useTasks';
import { useCompany } from '@/hooks/useCompany';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Plus, Calendar as CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const priorityOptions: { value: TaskPriority; label: string }[] = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
];

const statusOptions: { value: TaskStatus; label: string }[] = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'em_andamento', label: 'Em Andamento' },
  { value: 'concluida', label: 'Concluída' },
  { value: 'cancelada', label: 'Cancelada' },
];

export default function TarefasPage() {
  const { currentCompany } = useCompany();
  const { isLoading, fetchTasks, createTask, updateTask } = useTasks();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    dueDate: null as Date | null,
    priority: 'media' as TaskPriority,
  });

  useEffect(() => {
    if (currentCompany) {
      loadTasks();
    }
  }, [currentCompany]);

  const loadTasks = async () => {
    if (!currentCompany) return;
    const data = await fetchTasks(currentCompany.id);
    setTasks(data);
  };

  const handleCreateTask = async () => {
    if (!currentCompany) return;

    const task = await createTask({
      companyId: currentCompany.id,
      title: newTask.title,
      description: newTask.description,
      dueDate: newTask.dueDate || undefined,
      priority: newTask.priority,
    });

    if (task) {
      setTasks([task, ...tasks]);
      setIsCreateDialogOpen(false);
      setNewTask({
        title: '',
        description: '',
        dueDate: null,
        priority: 'media',
      });
    }
  };

  const handleUpdateTaskStatus = async (taskId: string, status: TaskStatus) => {
    const task = await updateTask(taskId, {
      status,
      completedAt: status === 'concluida' ? new Date() : undefined,
    });

    if (task) {
      setTasks(tasks.map(t => (t.id === taskId ? task : t)));
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'pendente':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'em_andamento':
        return 'bg-blue-500/10 text-blue-500';
      case 'concluida':
        return 'bg-green-500/10 text-green-500';
      case 'cancelada':
        return 'bg-red-500/10 text-red-500';
    }
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case 'baixa':
        return 'bg-gray-500/10 text-gray-500';
      case 'media':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'alta':
        return 'bg-red-500/10 text-red-500';
    }
  };

  if (!currentCompany) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <p className="text-muted-foreground">
          Selecione uma empresa para visualizar as tarefas
        </p>
      </div>
    );
  }

  return (
    <div className="container py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Tarefas</h1>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nova Tarefa
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar Nova Tarefa</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título</Label>
                <Input
                  id="title"
                  value={newTask.title}
                  onChange={e =>
                    setNewTask({ ...newTask, title: e.target.value })
                  }
                  placeholder="Digite o título da tarefa"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={newTask.description}
                  onChange={e =>
                    setNewTask({ ...newTask, description: e.target.value })
                  }
                  placeholder="Digite a descrição da tarefa"
                />
              </div>
              <div className="space-y-2">
                <Label>Data de Vencimento</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !newTask.dueDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newTask.dueDate ? (
                        format(newTask.dueDate, 'PPP', { locale: ptBR })
                      ) : (
                        <span>Selecione uma data</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={newTask.dueDate || undefined}
                      onSelect={date =>
                        setNewTask({ ...newTask, dueDate: date })
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Prioridade</Label>
                <Select
                  value={newTask.priority}
                  onValueChange={value =>
                    setNewTask({
                      ...newTask,
                      priority: value as TaskPriority,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorityOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleCreateTask} className="w-full">
                Criar Tarefa
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {isLoading ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground">
                Carregando tarefas...
              </p>
            </CardContent>
          </Card>
        ) : tasks.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground">
                Nenhuma tarefa encontrada
              </p>
            </CardContent>
          </Card>
        ) : (
          tasks.map(task => (
            <Card key={task.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl">{task.title}</CardTitle>
                    {task.dueDate && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Vence em{' '}
                        {format(new Date(task.dueDate), 'PPP', {
                          locale: ptBR,
                        })}
                      </p>
                    )}
                  </div>
                  <Select
                    value={task.status}
                    onValueChange={value =>
                      handleUpdateTaskStatus(task.id, value as TaskStatus)
                    }
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {task.description && (
                  <p className="text-muted-foreground mb-4">
                    {task.description}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                      task.status
                    )}`}
                  >
                    {
                      statusOptions.find(
                        option => option.value === task.status
                      )?.label
                    }
                  </span>
                  {task.priority && (
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(
                        task.priority
                      )}`}
                    >
                      {
                        priorityOptions.find(
                          option => option.value === task.priority
                        )?.label
                      }
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
} 